import { ThemeProvider } from "@emotion/react"
import { createTheme } from "@mui/material"
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table"
import React, { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { selectUser } from "../Redux/userSlice"
import { bgLineGridColorDark, bgLineGridColorLight, corBgGrid } from "../../Utils/Types"
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR"
import moment from "moment"
import { Funcoes } from "../../Utils/Funcoes"

interface RowData {
    dataEmissao: string,
    dataVencimento: string,
    dataPagamento: string,
    observacao: string,
    idFornecedor: number,
    nomeFornecedor: string,
    valor: number,
    ndup: string,
    serie: number
}

export type DadosRelCtaPagarDetalhado = {
    dataEmissao: string,
    dataVencimento: string,
    dataPagamento: string,
    observacao: string,
    idFornecedor: number,
    nomeFornecedor: string,
    valor: number,
    ndup: string,
}

type VisibilityState = Record<string, boolean>

type Props = {
    duplicatas: DadosRelCtaPagarDetalhado[],
    tipoFiltro: string,
}

export const GridCtaPagarDetalhado = React.memo(({ duplicatas, tipoFiltro }: Props) => {

    const defaultJson = useMemo(() => {
        return (

            {
                dataEmissao: true,
                dataVencimento: true,
                observacao: true,
                idFornecedor: true,
                nomeFornecedor: true,
                valor: true,
                ndup: true,
                serie: true
            }
        )

    }, [])

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(defaultJson)
    const [duplicata, setDuplicata] = useState<DadosRelCtaPagarDetalhado[]>([])

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- EFFECT -------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    useEffect(() => {
        setDuplicata(duplicatas)
    }, [duplicatas])

    useEffect(() => {
        setDuplicata([])
        const timer = setTimeout(() => {
            const newColumnVisibility = { ...columnVisibility }

            // Só define essas se houver id_select
            if (tipoFiltro === 'L') {
                newColumnVisibility.dataPagamento = true
            } else {
                newColumnVisibility.dataPagamento = false
            }

            setColumnVisibility(newColumnVisibility)
            clearTimeout(timer)
        }, 150)

        return () => clearTimeout(timer)

    }, [tipoFiltro])

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TEMAS --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    let user = useSelector(state => selectUser(state))

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'

    const theme = createTheme({
        palette: {
            mode: user.modeDark,

            background: {
                default:
                    user.modeDark === 'light'
                        ? '#fff' // modo claro
                        : '#475569', //modo escuro
            },
        },
    })


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- COLUNAS ------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const colunas = useMemo<MRT_ColumnDef<DadosRelCtaPagarDetalhado>[]>(
        () => [

            {
                accessorKey: 'ndup', header: 'N. Doc.', size: 30, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },
 
                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.ndup ? rowData.ndup : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

            {
                accessorKey: 'serie', header: 'Série', size: 50, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.serie ? rowData.serie : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

            {
                accessorKey: 'dataEmissao', header: 'Data Emissão', size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.dataEmissao ? moment(rowData.dataEmissao).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

            {
                accessorKey: 'dataVencimento', header: 'Data Vencimento', size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.dataVencimento ? moment(rowData.dataVencimento).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

            {
                accessorKey: 'dataPagamento', header: 'Data de Liquidação', visibleInShowHideMenu: false, size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (
                        <span>{rowData.dataPagamento ? moment(rowData.dataPagamento).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>
                    )

                }
            },

            {
                accessorKey: 'valor', header: 'Valor', size: 50, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.valor ? Funcoes.numberFormat(rowData.valor, 'currency') : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

            {
                accessorKey: 'nomeFornecedor', header: 'Fornecedor', size: 300, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.nomeFornecedor ? rowData.nomeFornecedor : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

            {
                accessorKey: 'observacao', header: 'Observação', size: 250, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

                Cell: (cell: any) => {

                    const rowData = cell.row.original as RowData
                    return (<span>{rowData.observacao ? rowData.observacao : <span className='text-red-400'>N/D</span>}</span>)

                }
            },

        ], [tipoFiltro]
    )

    const table = useMaterialReactTable({
        columns: colunas,
        data: duplicata,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,

        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        localization: MRT_Localization_PT_BR,
        paginationDisplayMode: 'pages',
        onColumnVisibilityChange: setColumnVisibility,
        state: { columnVisibility },

    })

    return <>
        <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
        </ThemeProvider>
    </>
})


